/* eslint-env node */

import _ from 'lodash';
import defaults from 'superagent-defaults';
import { menuUrl } from './api';
import {
  sanitize,
  isEngraving,
  productSkuForEngraving
} from 'shared/models/product';

export let cartRequest = defaults(require('superagent-bluebird-promise'));
reset();

function reset() {
  cartRequest.clear();
  cartRequest.set('x-platform', 'web');
  cartRequest.query({ lat: 1 });
}

export function addItemsToCart(items, opts = { checkout: false }) {
  const checkout = opts.checkout;
  const shipping_name = opts.shipping_name;
  let params = { skus: items, checkout };
  if (shipping_name) params.shipping_name = shipping_name;
  if (opts.collection)
    params.group_mapping = items.reduce((map, item) => {
      map[item.sku] = opts.collection.sku_id;
      return map;
    }, {});
  return cartRequest
    .post(menuUrl + 'cart')
    .send(params)
    .then(sanitizeCartProducts);
}

export function addItemToCart(sku, qty, opts = {}) {
  const item = { sku, qty };
  if (opts.engraving)
    item.meta = {
      engraving: opts.engraving
    };
  return addItemsToCart([item], opts);
}

export function updateCartItems(items, opts = { checkout: false }) {
  const checkout = opts.checkout;
  const shipping_name = opts.shipping_name;
  let params = { skus: items, checkout };
  if (shipping_name) params.shipping_name = shipping_name;
  return cartRequest
    .patch(menuUrl + 'cart')
    .send(params)
    .then(sanitizeCartProducts);
}

let _currentUpdate;
export function updateCartItem({ cart_id, sku_id }, qty, opts = {}) {
  if (_currentUpdate && _currentUpdate.isPending()) _currentUpdate.cancel();
  const item = cart_id ? { cart_id, qty } : { sku: sku_id, qty };
  if (opts.engraving)
    item.meta = {
      engraving: opts.engraving
    };
  _currentUpdate = updateCartItems([item], opts);
  return _currentUpdate;
}

export function removeCartItems(items, opts = { checkout: false }) {
  const checkout = opts.checkout;
  const shipping_name = opts.shipping_name;
  let params = { checkout };
  if (items.some(item => !!item.cart_id)) {
    params.ids = items.filter(item => !!item.cart_id).map(item => item.cart_id);
  } else {
    params.skus = items.filter(item => !!item.sku_id).map(item => item.sku_id);
  }
  if (shipping_name) params.shipping_name = shipping_name;
  return cartRequest
    .del(menuUrl + 'cart')
    .send(params)
    .then(sanitizeCartProducts);
}

export function removeCartItem({ cart_id, sku_id }, opts = {}) {
  const item = cart_id ? { cart_id } : { sku_id };
  return removeCartItems([item], opts);
}

export function getCart() {
  return cartRequest.get(menuUrl + 'cart').then(sanitizeCartProducts);
}

export function syncCart() {
  return cartRequest
    .post(menuUrl + 'cart/sync')
    .send({ checkout: true })
    .then(sanitizeCartProducts);
}

export function addPromoToCart(promo, { checkout } = { checkout: false }) {
  let data = { checkout };
  if (promo.value_type === 'credit') data.credit = promo;
  else data.code = promo.code;
  return cartRequest
    .post(menuUrl + 'cart/promo')
    .send(data)
    .then(sanitizeCartProducts);
}

export function removeCartPromos({ checkout } = { checkout: false }) {
  return cartRequest
    .post(menuUrl + 'cart/promo')
    .send({ code: null, checkout })
    .then(sanitizeCartProducts);
}

export function shippingOptions() {
  return cartRequest.get(menuUrl + 'cart/shipping').then(res => res.body);
}

export function updateShippingOption(option, opts = {}) {
  let params = { shipping_name: option.name };
  if (typeof opts.checkout !== 'undefined') params.checkout = opts.checkout;
  return cartRequest
    .post(menuUrl + 'cart/shipping')
    .send(params)
    .then(sanitizeCartProducts);
}

export function sanitizeCartProducts(res = {}) {
  let body = res.body;
  if (!body) return res;
  if (!body.products) return body;
  const engravings = body.products.filter(isEngraving);
  if (engravings) body.engravings = engravings;
  const products = body.products.filter(p => !isEngraving(p));
  if (products)
    body.products = products.map(sanitize).map(p => withSubtotal(p, p.qty));
  return body;
}

export function withSubtotal(cartItem, quantity) {
  let subtotal = (cartItem.price || 0) * quantity;
  let originalSubtotal = null;

  if (cartItem.package_qty) {
    originalSubtotal = subtotal;
    const qtyWithoutDiscount = quantity % cartItem.package_qty;
    const qtyWithDiscount = quantity - qtyWithoutDiscount;
    if (qtyWithDiscount) {
      subtotal =
        qtyWithoutDiscount * cartItem.price +
        qtyWithDiscount * cartItem.package_item_price;
    }
  }

  return {
    ...cartItem,
    qty: quantity,
    subtotal,
    originalSubtotal
  };
}

export function engravingForItem(engravings = [], item) {
  return _.find(engravings, engraving => {
    if (engraving.master_id) {
      return engraving.master_id === item.cart_id;
    } else if (engraving.master_sku) {
      return engraving.master_sku === item.sku_id && engraving.qty === item.qty;
    } else {
      return (
        productSkuForEngraving(engraving) === item.sku_id &&
        engraving.qty === item.qty
      );
    }
  });
}

export function subtotalForCart({ products, engravings }) {
  return (products || [])
    .concat(engravings || [])
    .filter(p => p.subtotal)
    .reduce((sum, p) => sum + p.subtotal, 0);
}
